/**
 * Renders everything that logically is a bot message.
 */
import React from "react";
import { connect } from "react-redux";

import styled from "styled-components";

import { device, typingDuration } from "../../lib/constants";
import { TypingAnimation } from "../../lib/animations";

import { deepChange, playBotMessageSound, scrollBottom } from "../../actions";
import { Show, urlConfig } from "../../lib/utils";
import { TextInputContainer } from "../InputField/DumbTextInput";
import { Answer } from "./Answer";
import { MessageContext } from "./BotMessageGenerator";
import { MESSAGE_SPACING_FACTOR } from "../../lib/enums";

/**
 * Bot Message component.
 */
class BotMessage extends React.Component {
  static contextType = MessageContext;

  /**
   * Constructor.
   */
  constructor(props, context) {
    super(props, context);
    const { dispatch } = props;

    const { isFeedback, messageNum, messageData } = this.context;

    let typing = true; // If should show typing animation.
    if (messageData.animationPassed || messageData.fromHistory || isFeedback) {
      typing = false;
    }

    if (urlConfig.noAnimation) {
      typing = false;
    }

    if (typing) {
      setTimeout(() => {
        this.setState({
          typing: false,
        });

        if (!isFeedback) {
          dispatch(playBotMessageSound());
        }

        dispatch(
          deepChange({
            path: ["messagesHistory", messageNum, "animationPassed"],
            value: true,
          })
        );

        window.famInputField && window.famInputField.focus();
        scrollBottom();
      }, typingDuration);
    }
    this.state = {
      typing,
    };
  }

  /**
   * Main message render method.
   */
  render() {
    const { isFeedback } = this.context;
    const { messageClass, RawContent, Actions, BubbleContent } = this.props;
    const { typing } = this.state;

    const doneTyping = !typing;
    const showSpacing = !Actions;
    const spacingMultiplier = isFeedback ? MESSAGE_SPACING_FACTOR.LARGE : MESSAGE_SPACING_FACTOR.DEFAULT;

    return (
      <MessageWrapper className={messageClass}>
        <Show when={RawContent}>{RawContent}</Show>

        <Show when={BubbleContent}>
          <MessageBubble typing={typing}>
            <Show when={typing}>
              <TypingAnimation />
            </Show>

            <Show when={doneTyping}>{BubbleContent}</Show>
          </MessageBubble>
        </Show>

        <Show when={doneTyping}>
          <Show when={Actions}>
            <ActionsContainer className={messageClass}>{Actions}</ActionsContainer>
          </Show>
          <Show when={showSpacing}>
            <Spacing multiplier={spacingMultiplier} />
          </Show>
        </Show>
      </MessageWrapper>
    );
  }
}

const Spacing = styled.div`
  padding-bottom: ${(props) => `${props.multiplier * 16}px`};
`;

export const MessageBubble = styled.div`
  background-color: #eaeff7;
  display: inline-block;
  position: relative;
  margin-bottom: 4px;
  border-radius: 24px 24px 24px 3px;
  padding: ${(props) => (props.typing ? "0px 16px" : "10px 21px 11px 16px")};
  line-height: 20px;
  margin-right: 40px;
  max-width: calc(100% - 40px);

  .text {
    font-size: ${(props) => props.theme.fontSize || "14px"};
    line-height: 1.4;
    font-weight: 400;
    letter-spacing: 0.3px;
    color: #282932;
  }

  svg {
    margin-bottom: -7px;
  }

  @media ${device.smallMobile} {
    .text {
      font-size: calc(${(props) => props.theme.fontSize || "14px"} - 1px);
    }
  }

  p:first-of-type {
    margin-block-start: 0;
  }

  p:last-of-type {
    margin-block-end: 0;
  }
`;

/**
 * For the TextInput component.
 * Moved here to prevent circular imports error.
 */
export const InputActionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  ${Answer} {
    width: calc(50% - 3px);
    margin: 8px 0 0;
    text-align: center;
    font-size: 17px;
    color: white;
    background-color: ${(props) => props.theme.brandColor};
    border-radius: 6px;

    &:hover {
      box-shadow: 0 4px 8px rgba(${(props) => props.theme.brandColorRGB}, 0.5);
    }

    &.cancel {
      background-color: white;
      border: 1px solid ${(props) => props.theme.brandColor};
      color: ${(props) => props.theme.brandColor};
    }
  }
`;

export const ActionsContainer = styled.div`
  padding: 0 0 20px;
  overflow: auto;

  &.multi-option {
    padding-left: 5px;
  }

  &:not(.last-message) ${Answer} {
    float: right;
  }

  ${TextInputContainer} {
    padding: 0;
    margin-top: 8px;
  }
`;

export const MessageWrapper = styled.div`
  width: 100%;

  &.from-history {
    ${Answer} {
      cursor: unset;

      :hover:not(.active) {
        :before {
          display: none;
        }
      }

      :hover:not(.active) .tick {
        background-color: white;
      }
    }
  }

  ${Answer} {
    &:hover {
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25);
    }
  }

  /**
    Styles for the last rendered bot message - the active one.
   */
  &.last-message {
    &.has-feedback {
      ${Spacing} {
        padding-bottom: 0;
      }
    }

    ${MessageBubble} {
      //margin-bottom: 40px;  // Disabled for the initial message animation.
    }

    ${ActionsContainer} {
      padding-bottom: 10px;
    }

    ${Answer} {
      width: 100%;
      display: block;
      text-align: center;
      font-size: 17px;
      color: white;
      background-color: ${(props) => props.theme.brandColor};
      border-radius: 6px;

      &:hover,
      .multi.bright {
        box-shadow: 0 4px 8px rgba(${(props) => props.theme.brandColorRGB}, 0.5);
      }
      &:active {
        background: ${(props) => props.theme.brandColorDarker};
      }
      &.multi {
        background-color: white;
        color: #595d61;
        display: inline-block;
        width: auto;
        border-radius: 23.5px;
        font-size: 12px;

        &.active {
          background: ${(props) => props.theme.brandColorDarker};
          color: white;
        }

        &:hover {
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25);
        }

        &.bright {
          background-color: ${(props) => props.theme.brandColor};
          color: white;
        }
      }
    }
  }
`;

export default connect(null, null, null, { pure: false })(BotMessage);
